import { Options, Vue } from "vue-class-component"
import { Emit, Prop, Watch } from "vue-property-decorator"
import { isSameDay } from "date-fns"
import { Action, Getter } from "s-vuex-class"

import { PokerCreateRequest, Team, UpdateObjectValue, User } from "@/models"
import { ToDo } from "@/models/ToDo"
import { ToDoContent } from "@/models/ToDoToggleContent"
import { ReferenceType } from "@/plugins/extensions/ReferenceType"
import { Actions, Getters } from "@/store"
import { addDays, getTodayCalendarDate, toCalendarDate } from "@/utils/dateUtil"

@Options({
  name: "ToDoCreateAndUpdate",
  components: {}
})
export default class extends Vue {
  @Getter(Getters.GET_TODO) readonly toDo: ToDo
  @Getter(Getters.GET_CURRENT_PROJECT_ID) currentProjectId: string
  @Getter(Getters.GET_ACCOUNT_ID) accountId: string
  @Getter(Getters.GET_SESSION_TEAM_ID) currentTeamId: string
  @Getter(Getters.IS_DEMO_PROJECT) demo: boolean

  @Action(Actions.UPDATE_TODO_VALUE) updateToDoValue: (payload: UpdateObjectValue<ToDo>) => void
  @Action(Actions.ADD_TODO) createToDo: (toDo: ToDo) => Promise<ToDo | false>
  @Action(Actions.UPDATE_TODO) updateToDo: (toDo: ToDo) => Promise<ToDo | false>
  @Action(Actions.CREATE_POKER) createPoker: (payload: PokerCreateRequest) => boolean
  @Action(Actions.GET_OPENED_TODO_COUNT) getOpenedToDoCount: () => void

  @Prop() widgetContent: ToDoContent

  today = false
  tomorrow = false
  users: User[] = []

  get estimatedDeadline() {
    return this.toDo.estimatedDeadline
  }

  get isEdited() {
    return this.toDo.isModified(this.toDo.changeByFields)
  }

  mounted() {
    if (!this.toDo.addMode) {
      this.toDo.setInitialState()
    }
    this.getDate()
    if (!this.toDo.id) {
      this.handleInput({
        id: "projectId",
        value: this.currentProjectId
      })
      if (!this.toDo.relatedResource?.id) {
        this.handleInput({
          id: "relatedResource",
          value: {
            id: this.accountId,
            type: "ACCOUNT"
          }
        })
      }
    }
  }

  @Watch("estimatedDeadline")
  getDate() {
    if (!this.estimatedDeadline) {
      this.today = false
      this.tomorrow = false
      return
    }

    const deadline = toCalendarDate(this.estimatedDeadline)
    const today = getTodayCalendarDate()
    this.today = isSameDay(deadline, today)
    this.tomorrow = isSameDay(deadline, addDays(today, 1))
  }

  /** Submits the form once while list state is updated by the response and subsequent SSE events. */
  @Emit("onSave")
  async onSave() {
    if (this.$wait.is("save.todo")) return false

    this.$wait.start("save.todo")
    try {
      if (this.toDo.isStoryPoker && this.currentTeamId) {
        const isCreated = await this.createPoker(
          new PokerCreateRequest({
            accountId: this.accountId,
            itemId: this.toDo.itemId,
            teamId: this.currentTeamId,
            estimatedDeadline: this.toDo.estimatedDeadline,
            createTodoItemRequest: this.toDo,
            asyncMode: true,
            demo: this.demo
          })
        )
        if (!isCreated) {
          return false
        }
        this.onCloseWidget()
        return true
      }
      if (!this.toDo.id) {
        const closeWidget = this.toDo.isStoryPoker || this.toDo.type === "TASK_BREAKDOWN"
        const createdToDo = await this.createToDo(this.toDo)
        if (!createdToDo) {
          return false
        }
        if (closeWidget) {
          this.onCloseWidget()
        } else {
          this.onCancel()
        }
        return true
      }
      const updatedToDo = await this.updateToDo(this.toDo)
      if (!updatedToDo) {
        return false
      }
      await this.getOpenedToDoCount()
      this.onCancel()
      return true
    } finally {
      this.$wait.end("save.todo")
    }
  }

  @Emit("onCancel")
  onCancel() {
    return
  }

  @Emit("onCloseWidget")
  onCloseWidget() {
    return
  }

  updateDate(date: Date | null) {
    if (date) {
      this.handleInput({
        id: "urgent",
        value: false
      })
    }
    this.handleInput({
      id: "estimatedDeadline",
      value: date
    })
  }

  onCheckboxToday(value: boolean) {
    if (value) {
      this.updateDate(getTodayCalendarDate())
      this.today = true
      this.tomorrow = false
    } else {
      this.updateDate(null)
      this.today = false
    }
  }

  onCheckboxTomorrow(value: boolean) {
    if (value) {
      this.updateDate(addDays(getTodayCalendarDate(), 1))
      this.tomorrow = true
      this.today = false
    } else {
      this.updateDate(null)
      this.tomorrow = false
    }
  }

  updateUrgent(value: boolean) {
    this.handleInput({
      id: "urgent",
      value: value
    })
    if (value) {
      this.updateDate(null)
      this.today = false
      this.tomorrow = false
    }
  }

  handleInputEditor(editorData) {
    this.handleInput({
      id: "structuredDescription",
      value: {
        html: editorData.html,
        json: JSON.stringify(editorData.json),
        text: editorData.getEmailText()
      }
    })
    this.handleInput({
      id: "fileMetadataIds",
      value: [
        ...editorData.attachments,
        ...editorData.refers.filter(refer => refer.type === ReferenceType.FILE)
      ].map(attachment => attachment.id)
    })
    const editorText = typeof editorData.text === "string" ? editorData.text.trim() : ""
    const rawFileName =
      editorData.refers.find(refer => refer.type === ReferenceType.FILE)?.attrs?.title ??
      editorData.attachments[0]?.attrs?.title
    const fileName = typeof rawFileName === "string" ? rawFileName : ""
    this.handleInput({
      id: "name",
      value: editorText || fileName
    })
  }

  onSelectedUsers(users: User[]) {
    this.handleInput({
      id: "owners",
      value: users
    })
  }

  onSelectedTeams(teams: Team[]) {
    this.handleInput({
      id: "selectedTeams",
      value: teams
    })
  }

  handleInput(payload) {
    this.updateToDoValue(payload)
    this.updateToDoValue({ id: "addMode", value: this.isEdited } as any)
  }
}
